import { useState } from 'react';
import {
  useActiveAccountLogo,
  useActivePractice,
  useCurrentUser,
  useDateFormatPreference,
  useProject,
} from '@witmetrics/data-store';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { showSuccessToaster } from '@/utils/toasters';
import TextField from '@/components/inputs/TextField';
import Dialog, {
  DefaultDialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/Dialog';
import DateTimeInput from '@/components/inputs/DateTimeInput';
import Title from '@/components/Title';
import Divider from '@/components/Divider';
import Button from '@/components/Button';
import PdfPreviewDialog from './PdfPreviewDialog';
import AttestPdf from './AttestPdf';
import type { TraceabilityData } from './types';
import { LABELS } from './constants';
import { buildInitialData, buildPdfFile, classes } from './utils';

export type TraceRecordDialogProps = {
  onSave: (file: File) => Promise<any>;
  onClose: () => void;
};

export default function TraceRecordDialog({
  onSave,
  onClose,
}: TraceRecordDialogProps) {
  const { activeProjectId, onApiError } = useAppState();
  const dateFormat = useDateFormatPreference();
  const logo = useActiveAccountLogo('/images/witmetrics_logo_h.png');
  const currentUser = useCurrentUser();
  const activePractice = useActivePractice();
  const activeProject = useProject(activeProjectId);
  const [formData, setFormData] = useState(
    buildInitialData(currentUser, activePractice, activeProject?.user)
  );
  const [isPreviewOpen, togglePreview] = useToggle();
  const [isSaving, toggleSaving] = useToggle();

  const handleSave = async () => {
    try {
      toggleSaving(true);
      const file = await buildPdfFile(
        <AttestPdf logoSrc={logo!} dateFormat={dateFormat} data={formData} />
      );
      await onSave(file);
      toggleSaving(false);
      showSuccessToaster('Record saved successfully');
      onClose();
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error saving record', () => handleSave());
    }
  };

  const renderField = (field: keyof TraceabilityData) => {
    if (field === 'expiryDate' || field === 'manufactureDate') {
      return (
        <DateTimeInput
          autoSet={false}
          disablePast={false}
          showTime={false}
          className={classes.input}
          label={LABELS[field]}
          value={formData[field]}
          onChange={(update) =>
            setFormData({ ...formData, [field]: update ?? '' })
          }
        />
      );
    } else {
      return (
        <TextField
          key={field}
          className={classes.input}
          label={LABELS[field]}
          value={formData[field]}
          onChange={(update) => setFormData({ ...formData, [field]: update })}
        />
      );
    }
  };

  return (
    <>
      {isPreviewOpen && (
        <PdfPreviewDialog
          logo={logo!}
          data={formData}
          onClose={() => togglePreview(false)}
        />
      )}
      <Dialog className={classes.dialog} onClose={onClose}>
        <DialogTitle title="New Traceability Record" onClose={onClose} />
        <DialogBody className={classes.body}>
          <div className={classes.section}>
            <Title className={classes.title} size="sm">
              Resin Information
            </Title>
            {renderField('resinName')}
            {renderField('lotNumber')}
            {renderField('expiryDate')}
          </div>
          <Divider flexItem orientation="vertical" />
          <div className={classes.section}>
            <Title className={classes.title} size="sm">
              Patient & Device Information
            </Title>
            {renderField('patientCode')}
            {renderField('orderNumber')}
            {renderField('orderDescription')}
            {renderField('manufactureDate')}
            {renderField('clinicName')}
            {renderField('address')}
            {renderField('ownerName')}
          </div>
        </DialogBody>
        <DefaultDialogActions
          disabled={false /* TODO */}
          pending={isSaving}
          confirmLabel="Save"
          pendingLabel="Saving"
          adornment={
            <Button
              className={classes.button}
              variant="secondary"
              onClick={() => togglePreview()}>
              Preview
            </Button>
          }
          onCancel={onClose}
          onConfirm={handleSave}
        />
      </Dialog>
    </>
  );
}
